/**
 * Corporate T&C accept · gate before dashboard access.
 *
 * Only reachable after sign-in. On accept, backend updates the member
 * row, re-issues the session JWT with `terms_version_accepted` claim
 * populated, then we redirect to the dashboard.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { corporateApi } from "@/lib/corporate-api";
import CorporateShell from "./CorporateShell";

const T = {
  bg: "#F4EFE6", card: "#FCFAF4", ink: "#211C15", ink2: "#5C5447",
  ink3: "#8A8071", line: "#E5DCCB", brass: "#8B6914", danger: "#B84B3C",
};

export default function CorporateTerms() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [me,      setMe]      = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await corporateApi.me();
        if (!alive) return;
        if (!res.needs_terms_accept) {
          navigate(`/${slug}/dashboard`, { replace: true });
          return;
        }
        setMe(res);
      } catch {
        if (alive) navigate(`/${slug}/sign-in`, { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  async function onAccept() {
    if (!checked || !me) return;
    setErr(null); setBusy(true);
    try {
      await corporateApi.acceptTerms(me.tenant.terms_version);
      navigate(`/${slug}/dashboard`, { replace: true });
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  if (!me) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh",
                    display: "flex", alignItems: "center",
                    justifyContent: "center", color: T.ink3 }}>
        Loading…
      </div>
    );
  }

  return (
    <CorporateShell tenant={me.tenant} member={me.member}>
      <div style={{ maxWidth: 640, margin: "24px auto 0" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px",
                    fontWeight: 700 }}>
          One-time terms · v{me.tenant.terms_version}
        </p>
        <h1 style={{ fontFamily: "'Fraunces', Georgia, serif",
                     fontSize: "clamp(28px, 4.4vw, 40px)", fontWeight: 500,
                     lineHeight: 1.1, color: T.ink, margin: "0 0 24px" }}>
          Before we begin.
        </h1>

        <div data-testid="corp-terms-body"
             style={{ background: T.card, border: `1px solid ${T.line}`,
                      borderRadius: 12, padding: "28px 32px",
                      lineHeight: 1.65, fontSize: 14.5, color: T.ink2 }}>
          <p style={{ margin: "0 0 16px" }}>
            <strong style={{ color: T.ink }}>
              This benefit is sponsored by {me.tenant.display_name}
            </strong>
            {" "}and remains available while you are employed there.
          </p>
          <p style={{ margin: "0 0 16px" }}>
            {me.tenant.display_name} sees aggregate usage patterns only —
            never the content of your individual requests. Everything you
            ask through this portal reaches the Les Concierges team
            directly, in confidence.
          </p>
          <p style={{ margin: "0 0 16px" }}>
            All bookings and requests are fulfilled by Les Concierges
            under our standard concierge terms. Some services (such as
            private members&apos; clubs) may be available separately as
            optional paid add-ons, clearly marked as such.
          </p>
          <p style={{ margin: 0 }}>
            You can stop using the portal at any time by signing out.
            Your access ends automatically when {me.tenant.display_name}
            confirms you have left the company.
          </p>
        </div>

        {err && (
          <div data-testid="corp-terms-err"
               style={{ background: "#FDECE9", border: `1px solid ${T.danger}55`,
                        borderRadius: 8, padding: "12px 14px",
                        marginTop: 20, color: T.danger, fontSize: 13 }}>
            {err}
          </div>
        )}

        <label
          data-testid="corp-terms-checkbox-label"
          style={{ display: "flex", alignItems: "flex-start", gap: 12,
                   marginTop: 24, cursor: "pointer",
                   fontSize: 14, color: T.ink2, lineHeight: 1.5 }}
        >
          <input
            data-testid="corp-terms-checkbox"
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginTop: 3, width: 18, height: 18, cursor: "pointer",
                     accentColor: T.brass }}
          />
          <span>
            I understand this is a sponsored benefit provided by{" "}
            <strong style={{ color: T.ink }}>{me.tenant.display_name}</strong>{" "}
            and I accept the terms above and the{" "}
            <a href="/legal" target="_blank" rel="noreferrer"
               style={{ color: T.brass }}>Les Concierges concierge terms</a>.
          </span>
        </label>

        <button
          data-testid="corp-terms-accept"
          type="button"
          disabled={!checked || busy}
          onClick={onAccept}
          style={{
            marginTop: 28, background: T.ink, color: T.bg,
            border: 0, borderRadius: 999, padding: "14px 28px",
            fontSize: 13, fontWeight: 700,
            letterSpacing: "0.10em", textTransform: "uppercase",
            fontFamily: "inherit",
            opacity: !checked || busy ? 0.55 : 1,
            cursor: !checked || busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Saving…" : "Accept & enter the portal →"}
        </button>
      </div>
    </CorporateShell>
  );
}
